import type { SidebarCategory, SidebarPage } from '@readme/iso';

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProjectContext } from '@core/context'; // eslint-disable-line no-restricted-imports
import useClassy from '@core/hooks/useClassy';
import useLocalStorage from '@core/hooks/useLocalStorage';
import useMediaQuery from '@core/hooks/useMediaQuery';
import usePlanPermissions from '@core/hooks/usePlanPermissions';
import { useProjectStore, useSuperHubStore } from '@core/store';
import { isApiConfigCategory } from '@core/store/SuperHub/Sidebar/util';
import classy from '@core/utils/classy';

import Button from '@ui/Button';
import Icon from '@ui/Icon';

import Group from './Group';
import SidebarList from './List';
import classes from './style.module.scss';
import './style.scss';
import useScrollToActiveSidebarItem from './useScrollToActiveSidebarItem';

export interface SidebarProps {
  /**
   * Slug for the current page.
   */
  activeDoc?: string;

  /**
   * Collection of ReadMe docs or endpoint reference pages.
   */
  categories: SidebarCategory[];

  className?: string;

  /**
   * Optional custom icon render function for the sidebar list items.
   */
  customIconRender?: (page: SidebarPage) => JSX.Element | null;

  /**
   * Optional sidebar header.
   */
  header?: React.ReactNode;

  /**
   * Optional id to be applied to the <nav> component.
   */
  id?: string;

  /**
   * Optional flag to indicate if the project uses the sidebar layout theme.
   */
  isSidebarLayout?: boolean;

  /**
   * Optional CSS class to be applied to the <nav> component.
   */
  navClass?: string;

  /**
   * Sidebar link prefix.
   */
  pathRoot?: 'docs' | 'edit' | 'reference';

  /**
   * Optional label and badge that triggers the top-level section header
   */
  sectionHeader?: {
    badge: React.ReactElement | null;
    label: string | null;
  };
}

const ModuleDisabled = () => (
  <div className="disabled-warning">
    <div>
      <i aria-hidden="true" className="icon icon-alert-circle" /> Only admins can see this
    </div>
    <Button fullWidth kind="destructive" rel="noreferrer" size="sm" target="_blank" to="/dash?to=usability#modules">
      Enable it for everyone
    </Button>
  </div>
);

const PoweredBy = () => {
  const { project } = useContext(ProjectContext);
  const { subdomain } = project;
  const { t } = useTranslation();

  return (
    <div className="readme-logo" id="readmeLogo">
      {t('poweredBy')}
      &nbsp;
      <a aria-label="ReadMe" href={`https://readme.com?ref_src=hub&project=${subdomain}`} style={{ color: 'unset' }}>
        <i className="icon icon-readme" style={{ display: 'inline-block', minWidth: 71 }} />
      </a>
    </div>
  );
};

const IPAD_MINI_PORTRAIT = '768px';

const Sidebar = React.memo(function Sidebar({
  activeDoc,
  categories,
  className,
  customIconRender,
  header,
  pathRoot,
  navClass = '',
  id,
  isSidebarLayout,
  sectionHeader,
}: SidebarProps) {
  const { project } = useContext(ProjectContext);
  const [isSuperHub, isSuperHubAdminOrEditor, routeSection, isEnterpriseChild, isAdminUIHidden] = useSuperHubStore(
    s => [
      s.isSuperHub,
      s.user.role === 'admin' || s.user.role === 'editor',
      s.routeSection,
      s.enterprise?.isChild,
      s.isAdminUIHidden,
    ],
  );
  const [hideReadmeLogo, activePlan] = useProjectStore(s => [
    s.data.appearance.footer.readme_logo === 'hide',
    s.plan.activePlan,
  ]);
  const projectCanHideLogo = usePlanPermissions(activePlan, 'whitelabel');
  let showPoweredByLogo = !(projectCanHideLogo && hideReadmeLogo);

  // Need to check if the parent project has the hide_logo flag set
  if (isEnterpriseChild && project.parent) {
    showPoweredByLogo = !project.parent.appearance.hide_logo;
  }

  const { scrollAreaRef, scrollContentRef, activeItemRef } = useScrollToActiveSidebarItem();
  const isMobile = useMediaQuery(`(max-width: ${IPAD_MINI_PORTRAIT})`);
  const isAskAIViewport = useMediaQuery('(min-width: 1400px) and (max-width: 1800px)');
  const bem = useClassy(classes, 'Nav');

  const storage = useLocalStorage({ json: true, prefix: 'sidebar-collapsed' });
  const isOwlbotOpen = useSuperHubStore(s => s.isOwlbotOpenCached);

  const [isCollapsed, setIsCollapsed] = useState(() => {
    return isMobile ? false : storage.getItem('sidebar-collapsed'); // never collapse the mobile flyout menu
  });

  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(false);
    } else {
      setIsCollapsed(storage.getItem('sidebar-collapsed'));
    }
  }, [isMobile, storage]);

  // Auto-collapse reference sidebar when Ask AI is open at 1400-1800px
  useEffect(() => {
    if (id !== 'reference-sidebar') return;

    if (isAskAIViewport && isOwlbotOpen) {
      // Auto-collapse when Ask AI opens in the target viewport range
      setIsCollapsed(true);
    } else if (!isAskAIViewport && isOwlbotOpen) {
      // When exiting the Ask AI viewport range (e.g., resizing to >1800px) while Ask AI is still open,
      // restore the user's previous preference
      setIsCollapsed(storage.getItem('sidebar-collapsed') || false);
    } else if (isAskAIViewport && !isOwlbotOpen) {
      // When Ask AI closes within the target viewport, restore the user's previous preference
      setIsCollapsed(storage.getItem('sidebar-collapsed') || false);
    }
  }, [id, isAskAIViewport, isOwlbotOpen, storage]);

  /**
   * If the user is a superhub admin or editor and NOT in preview mode, show all categories.
   * Otherwise (non-admin/editor OR in preview mode), only show categories with visible pages.
   */
  const filteredCategories = useMemo(() => {
    const orderedCategories = [...categories];

    // Show all pages if admin and NOT in preview mode
    const shouldShowHidden = isSuperHubAdminOrEditor && !isAdminUIHidden;

    if (shouldShowHidden) {
      return orderedCategories;
    }

    const hasNestedPages = (page: SidebarPage): page is SidebarPage & { pages: SidebarPage[] } => {
      return 'pages' in page && Array.isArray(page.pages);
    };

    // Recursively filter hidden pages at all nesting levels
    const filterPages = (pages: SidebarPage[]): SidebarPage[] => {
      return pages
        .filter(page => !page.hidden)
        .map(page => {
          if (hasNestedPages(page)) {
            return {
              ...page,
              pages: filterPages(page.pages),
            };
          }
          return page;
        }) as SidebarPage[];
    };

    // Filter out hidden pages and empty categories
    return orderedCategories
      .map(category => ({
        ...category,
        pages: filterPages(category.pages),
      }))
      .filter(category => category.pages.length > 0);
  }, [categories, isSuperHubAdminOrEditor, isAdminUIHidden]);

  const SidebarToggle = useMemo(() => {
    const toggleSidebar = (collapsed: boolean) => {
      setIsCollapsed(collapsed);
      storage.setItem('sidebar-collapsed', collapsed);
    };

    return isCollapsed ? (
      <Button
        aria-label="Show sidebar navigation"
        className={bem('-toggle-expand')}
        ghost
        kind="secondary"
        onClick={() => toggleSidebar(false)}
        size="sm"
      >
        <Icon aria-hidden="true" name="sidebar" size="md" />
      </Button>
    ) : (
      <button
        aria-label="Hide sidebar navigation"
        className={bem('-toggle-collapse')}
        onClick={() => toggleSidebar(true)}
        type="button"
      />
    );
  }, [isCollapsed, bem, storage]);

  const navClasses = bem(
    'rm-Sidebar',
    'hub-sidebar',
    'reference-redesign',
    className,
    '&',
    isSidebarLayout && '_sidebar-layout',
    !isSidebarLayout && pathRoot === 'docs' && ' rm-Sidebar_guides',
    navClass && navClass,
    isMobile && classes.Sidebar_flyout,
    isCollapsed && bem('-collapsed'),
  );

  const categoriesArray = () => {
    return filteredCategories.map(({ title, pages, slug, uri }) => {
      const hideCategoryTitle =
        isSuperHub && pathRoot !== 'edit' && isApiConfigCategory(pathRoot || routeSection, title, pages);

      return (
        <section key={slug ?? uri} className={`${classes['Sidebar-listWrapper']} rm-Sidebar-section`}>
          {!hideCategoryTitle && (
            <h2
              className={classy(
                classes['Sidebar-heading'],
                isSidebarLayout && classes['Sidebar-heading_sidebarLayout'],
                'rm-Sidebar-heading',
              )}
            >
              {title}
            </h2>
          )}
          <SidebarList
            activeItemRef={activeItemRef}
            alwaysExpanded={isMobile}
            className={bem('Sidebar-list_sidebarLayout')}
            {...{ activeDoc, customIconRender, pathRoot, pages }}
          />
        </section>
      );
    });
  };

  if (isSidebarLayout) {
    return <Group sectionHeader={sectionHeader}>{Array.isArray(filteredCategories) && categoriesArray()}</Group>;
  }

  return (
    <nav
      ref={scrollAreaRef}
      aria-label="Secondary navigation"
      className={navClasses}
      id={id || 'hub-sidebar'}
      role="navigation"
    >
      {!isCollapsed && header}

      <div
        ref={scrollContentRef}
        className={classy(
          classes.Sidebar,
          isCollapsed && classes['Sidebar-collapsed'],
          'rm-Sidebar',
          'hub-sidebar-content',
        )}
      >
        {!isSuperHub && !(project?.modules?.[pathRoot || ''] || pathRoot === 'edit') && <ModuleDisabled />}
        {Array.isArray(filteredCategories) && categoriesArray()}
        {!!showPoweredByLogo && <PoweredBy />}
      </div>

      {SidebarToggle}
    </nav>
  );
});

export default Sidebar;
